/**
 * Transactions Tab - General Ledger Transaction History
 *
 * Shows a chronological list of all fund transactions:
 * - Drawdowns (capital calls from LPs)
 * - Investment purchases (portfolio investments)
 * - Fund fees & expenses
 * - Distributions (returns to LPs)
 * - LP transfers (ownership changes)
 */

import { useState, useCallback } from 'react';
import { Badge } from '../ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { formatDate, formatCurrency } from '../../utils/formatters';
import { useTransactions } from '../../hooks/react-query/useGeneralLedger';
import { TransactionDetailModal } from './TransactionDetailModal';
import {
  TransactionFilterBar,
  type TransactionFilters,
} from './TransactionFilterBar';
import { TransactionPagination } from './TransactionPagination';
import { type Transaction } from '../../types/drawdown';
import {
  getTypeLabel,
  getStatusBadgeStyle,
  getStatusLabel,
} from './transaction-labels';
import { Loader2 } from 'lucide-react';

interface TransactionsTabProps {
  fundId?: string;
}

export function TransactionsTab({ fundId }: TransactionsTabProps) {
  const [selectedTransactionId, setSelectedTransactionId] = useState<
    string | null
  >(null);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<TransactionFilters>({});
  const pageSize = 50;

  const handleFiltersChange = useCallback((newFilters: TransactionFilters) => {
    setFilters(newFilters);
    setPage(1);
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({});
    setPage(1);
  }, []);

  const { data, isLoading, error } = useTransactions(fundId, {
    page,
    page_size: pageSize,
    ...filters,
  });

  const transactions = data?.transactions ?? [];
  const totalCount = data?.total_count ?? 0;

  const handleRowClick = (transaction: Transaction) => {
    setSelectedTransactionId(transaction.id);
  };

  const handleModalClose = () => {
    setSelectedTransactionId(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12 text-red-500">
        <p>Error loading transactions: {error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <TransactionFilterBar
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onClearFilters={handleClearFilters}
      />

      {transactions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
          <p>No transactions found</p>
          <p className="text-sm mt-2">
            Transactions will appear here when drawdowns or other financial
            events occur.
          </p>
        </div>
      ) : (
        <>
          <div className="border rounded-lg bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">DATE</TableHead>
                  <TableHead className="w-[120px]">REFERENCE</TableHead>
                  <TableHead className="w-[110px]">TYPE</TableHead>
                  <TableHead>DESCRIPTION</TableHead>
                  <TableHead className="text-right w-[140px]">AMOUNT</TableHead>
                  <TableHead className="w-[90px]">STATUS</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.map((tx) => (
                  <TableRow
                    key={tx.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleRowClick(tx)}
                  >
                    <TableCell className="text-muted-foreground">
                      {formatDate(tx.transaction_date)}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {tx.reference || '-'}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className="bg-muted text-muted-foreground border-border"
                      >
                        {getTypeLabel(tx.transaction_type)}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium">
                      {tx.description}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(tx.total_amount, {
                        currency: tx.currency,
                      })}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={getStatusBadgeStyle(tx.status)}
                      >
                        {getStatusLabel(tx.status)}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <TransactionPagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
          />
        </>
      )}

      <TransactionDetailModal
        fundId={fundId}
        transactionId={selectedTransactionId}
        onClose={handleModalClose}
      />
    </div>
  );
}
